import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 100).bind(this);

  #handleQuantitySelectorUpdate = (event) => {
    if (event.detail?.quantity === 0) {
      this.#onQuantityChange(event);
      return;
    }

    this.#debouncedOnChange(event);
  };

  /** @type {AbortController | null} */
  #cartUpdateAbortController = null;

  /** @type {number} */
  #cartUpdateRequestId = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.#cartUpdateAbortController?.abort();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      this.#optimisticallyRemoveLine(line);
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Gives delete actions an instant visual response while Shopify confirms the cart change.
   * @param {number} line - The line item index.
   */
  #optimisticallyRemoveLine(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    lineItemRow.style.setProperty('--row-height', `${lineItemRow.offsetHeight}px`);
    lineItemRow.setAttribute('aria-hidden', 'true');
    lineItemRow.classList.add('removing');

    for (const control of lineItemRow.querySelectorAll('button, input, select, textarea, a')) {
      if ('disabled' in control) {
        control.disabled = true;
      }
      control.setAttribute('tabindex', '-1');
    }
  }

  /**
   * Restores an optimistically hidden line when Shopify rejects the update.
   * @param {number} line - The line item index.
   */
  #restoreOptimisticLine(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    lineItemRow.classList.remove('removing');
    lineItemRow.removeAttribute('aria-hidden');
    lineItemRow.style.removeProperty('--row-height');

    for (const control of lineItemRow.querySelectorAll('button, input, select, textarea, a')) {
      if ('disabled' in control) {
        control.disabled = false;
      }
      control.removeAttribute('tabindex');
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const requestId = ++this.#cartUpdateRequestId;

    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = new AbortController();
    this.#disableCartItems();

    const { line, quantity } = config;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal: this.#cartUpdateAbortController.signal,
    })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== this.#cartUpdateRequestId) return;

        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          this.#restoreOptimisticLine(line);
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);
        this.#updateVisibleCartTotal(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: parsedResponseText.item_count,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (parsedResponseText.sections?.[this.sectionId]) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer
            .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
            .catch((error) => console.error(error));
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.#restoreOptimisticLine(line);
        console.error(error);
      })
      .finally(() => {
        if (requestId === this.#cartUpdateRequestId) {
          this.#enableCartItems();
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        }
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates visible cart totals from the lightweight Ajax cart response while the section refreshes.
   * @param {Object} updatedCart - The updated cart object.
   * @param {number} [updatedCart.total_price] - The cart total in minor units.
   */
  #updateVisibleCartTotal(updatedCart) {
    if (typeof updatedCart.total_price !== 'number') return;

    const formattedTotal = this.#formatCartMoney(updatedCart.total_price);
    const cartTotal = this.refs.cartTotal;
    const totalNodes = new Set([
      cartTotal,
      ...document.querySelectorAll('[data-cart-subtotal], [data-testid="cart-total-value"]'),
    ]);

    totalNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = formattedTotal;
      node.setAttribute('value', formattedTotal);
    });
  }

  /**
   * Formats a cart total using the current cart total text as a template.
   * @param {number} value - The cart total in minor units.
   * @param {string} [template] - Optional formatted money string to copy currency styling from.
   * @returns {string}
   */
  #formatCartMoney(value, template) {
    const sample = template?.trim() || this.refs.cartTotal?.textContent?.trim() || this.refs.cartTotal?.getAttribute('value') || '$0.00 USD';
    const currencyMatch = sample.match(/\s[A-Z]{3}$/);
    const currency = currencyMatch?.[0] || '';
    const prefixMatch = sample.match(/^[^\d-]+/);
    const prefix = prefixMatch?.[0] || '$';

    return `${prefix}${(value / 100).toFixed(2)}${currency}`;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
